import React, { useState, useEffect, useCallback } from "react";
import styles from "./index.module.css";
import Header from "../../elements/Header";
import { userAxios } from "../../../utils/axios/user.axios";
import { useSelector } from "react-redux";
import PulseLoader from "react-spinners/PulseLoader";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

const ROWS_PER_PAGE = 10;
const FIXED_HEADERS = ["SKU", "Product Title", "Brand", "Price", "Image URL", "Product URL"];

const FALLBACK_RATES = {
  NZD: 0.58,
  AUD: 0.7,
  SGD: 0.78,
};

const isImageColumn = (header) => {
  const h = (header || "").toLowerCase();
  return h.includes("image") || h === "img";
};

const isProductUrlColumn = (header) => {
  const h = (header || "").toLowerCase();
  return h.includes("url") && h.includes("product");
};

const isPriceColumn = (header) => {
  const h = (header || "").toLowerCase();
  return h === "price" || h.includes("price");
};

const isUrl = (val) => {
  if (val == null || typeof val !== "string") return false;
  const s = val.trim();
  return s.startsWith("http://") || s.startsWith("https://");
};

/**
 * Stored price is USD. Convert back to display currency.
 * For NZD, prefer the row's import-time Conversion Rate (same as admin).
 */
function formatPriceDisplay(val, currency, conversionRateNzd) {
  if (val == null || String(val).trim() === "") return "";
  const s = String(val).replace(/,/g, "").trim();
  const parts = s
    .split(/\s*-\s*/)
    .map((p) => parseFloat(p.replace(/[^\d.]/g, "")))
    .filter((n) => !isNaN(n) && n > 0);
  if (parts.length === 0) return s;

  const displayCurrency = currency || "NZD";
  const symbol =
    displayCurrency === "AUD" ? "A$" : displayCurrency === "SGD" ? "S$" : "NZ$";

  let usdPerLocal = FALLBACK_RATES[displayCurrency] || FALLBACK_RATES.NZD;
  if (displayCurrency === "NZD" && Number(conversionRateNzd) > 0) {
    usdPerLocal = Number(conversionRateNzd);
  }

  const converted = parts.map((n) => (n / usdPerLocal).toFixed(2));
  return converted.map((c) => symbol + c).join(" - ");
}

const PAGE_CHANGE_DELAY_MS = 500;
const SITE_OPTIONS = [
  { value: "farmers", label: "Farmers" },
  { value: "chemist_warehouse", label: "Chemist Warehouse" },
];

const normalize = (val) => String(val || "").trim().toLowerCase();
const EXCEL_SHEET_MAX_LEN = 31;

const rowSiteMatches = (row, selectedSite) => {
  if (!row || !selectedSite) return false;

  const explicitSiteValue = normalize(
    row.Site || row.site || row.Source || row.source || row.Store || row.store || row.Website || row.website
  );

  if (explicitSiteValue) {
    if (selectedSite === "farmers") {
      return explicitSiteValue.includes("farmers");
    }
    return explicitSiteValue.includes("chemist") || explicitSiteValue.includes("warehouse");
  }

  const productUrl = normalize(row["Product URL"] || row.productUrl || row.product_url || row.url);
  if (!productUrl) return false;

  if (selectedSite === "farmers") {
    return productUrl.includes("farmers.co.nz");
  }
  return productUrl.includes("chemistwarehouse.co.nz");
};

const ChivasDior = () => {
  const currency = useSelector((store) => store.constant.currency);
  const [tableData, setTableData] = useState([]);
  const [selectedSite, setSelectedSite] = useState("farmers");
  const [currentPage, setCurrentPage] = useState(1);
  const [listLoading, setListLoading] = useState(true);
  const [pageTransitioning, setPageTransitioning] = useState(false);

  const itemsPerPage = ROWS_PER_PAGE;

  const fetchList = useCallback(async () => {
    setListLoading(true);
    try {
      const res = await userAxios.get("import-chivas-dior/list-public");
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
      setTableData(list);
      setCurrentPage(1);
    } catch {
      setTableData([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const baseData = Array.isArray(tableData) ? tableData : [];
  const data = baseData.filter((row) => rowSiteMatches(row, selectedSite));
  const totalRecords = data.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / itemsPerPage));
  const start = (currentPage - 1) * itemsPerPage;
  const currentPageData = data.slice(start, start + itemsPerPage);
  const selectedSiteLabel = SITE_OPTIONS.find((opt) => opt.value === selectedSite)?.label || "";

  const goToPage = (pageNum) => {
    if (pageNum === currentPage || pageTransitioning) return;
    setPageTransitioning(true);
    setTimeout(() => {
      setCurrentPage(pageNum);
      setPageTransitioning(false);
    }, PAGE_CHANGE_DELAY_MS);
  };

  const handleDownloadExcel = () => {
    if (data.length === 0) {
      toast.error("No data to export.");
      return;
    }
    const ws_data = [FIXED_HEADERS];
    data.forEach((row) => {
      const excelRow = FIXED_HEADERS.map((h) => {
        const val = row[h];
        if (h === "Price") {
          return formatPriceDisplay(val, currency, row["Conversion Rate"]);
        }
        return val != null ? String(val) : "";
      });
      ws_data.push(excelRow);
    });
    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    const wb = XLSX.utils.book_new();
    const rawSheetName = `Chanel Dior - ${selectedSiteLabel}`;
    const safeSheetName = rawSheetName.slice(0, EXCEL_SHEET_MAX_LEN);
    XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const fileSiteName = selectedSiteLabel.toLowerCase().replace(/\s+/g, "-");
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), `chanel-dior-products-${fileSiteName}.xlsx`);
    toast.success("Excel downloaded!");
  };

  const renderCell = (h, val, row) => {
    const str = val != null ? String(val) : "";
    if (isImageColumn(h) && isUrl(str)) {
      return (
        <div className={styles.cell_image}>
          <img
            src={str}
            alt=""
            loading="lazy"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </div>
      );
    }
    if (isProductUrlColumn(h) && isUrl(str)) {
      return (
        <a
          href={str}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.productLink}
        >
          View product
        </a>
      );
    }
    if (isPriceColumn(h)) {
      return formatPriceDisplay(val, currency, row?.["Conversion Rate"]);
    }
    return str;
  };

  return (
    <div className={styles.container}>
      <Header isDashboard={true} />
      <hr />
      <div className={styles.heading}>
        <h3>Chanel and Dior</h3>
        <span>Imported products by selected site. Prices use the currency selected in the header.</span>
      </div>
      {listLoading && data.length === 0 ? (
        <div className={styles.tableEmpty}>Loading…</div>
      ) : data.length > 0 ? (
        <div className={styles.price_changes_section}>
          <div className={styles.price_changes_header}>
            <h2>Chanel and Dior Products</h2>
            <div className={styles.actions}>
              <div className={styles.siteFilter}>
  
                <select
                  id="siteFilter"
                  value={selectedSite}
                  className={styles.siteFilterSelect}
                  onChange={(e) => {
                    setSelectedSite(e.target.value);
                    setCurrentPage(1);
                  }}
                  disabled={listLoading}
                >
                  {SITE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <button type="button" className={styles.downloadExcelButton} onClick={handleDownloadExcel}>
                Download Excel
              </button>
            </div>
          </div>
          <div className={styles.table_container}>
            <div className={styles.table}>
              <div className={styles.thead}>
                {FIXED_HEADERS.map((h) => (
                  <div key={h} className={styles.thead_cell}>
                    {h}
                  </div>
                ))}
              </div>
              {listLoading ? (
                <div className={styles.loader_container}>
                  <PulseLoader color="#028d7f" size={12} />
                </div>
              ) : (
                <div className={styles.tdata}>
                  {currentPageData.map((row, i) => (
                    <div className={styles.trow} key={start + i}>
                      {FIXED_HEADERS.map((h) => (
                        <div key={h} className={styles.trow_cell}>
                          {renderCell(h, row[h], row)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          {totalRecords > itemsPerPage && (
            <div className={styles.pagination}>
              <div className={styles.pagination_info}>
                Showing {start + 1} to {Math.min(start + itemsPerPage, totalRecords)} of {totalRecords} results
              </div>
              <div className={styles.pagination_controls}>
                <button
                  type="button"
                  className={styles.pagination_button}
                  disabled={currentPage === 1 || pageTransitioning}
                  onClick={() => goToPage(currentPage - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      type="button"
                      key={pageNum}
                      className={`${styles.pagination_button} ${styles.pagination_number} ${
                        currentPage === pageNum ? styles.active : ""
                      }`}
                      disabled={pageTransitioning}
                      onClick={() => goToPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className={styles.pagination_button}
                  disabled={currentPage === totalPages || pageTransitioning}
                  onClick={() => goToPage(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className={styles.tableEmpty}>No data.</div>
      )}
    </div>
  );
};

export default ChivasDior;